import { ColumnDef } from '@tanstack/react-table';
import {
  Copy,
  DotsThreeVertical,
  Scales,
  UserGear,
  UserMinus,
  UserPlus,
} from '@phosphor-icons/react';
import { DataTableColumnHeader, InlineSkeleton } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { useIsMobile } from '@/hooks/use-media-query';
import { Employee } from '../../types/employee.types';
import { WfhCell } from '../work-status/work-status-cells';
import { employeeName } from '../../schemas/display-name';

/**
 * The work from home switch. The column is only passed in for someone holding
 * `DIRECTORY.CHANGE_WFH`, so nobody else sees a control they cannot use.
 */
interface WorkStatusColumnsProps {
  /** Id of the employee whose change is being saved right now, if any. */
  savingEmployeeId?: string;
  onWfhChange: (employee: Employee, next: boolean) => void;
  onOfficeDaysEdit: (employee: Employee) => void;
}

interface CreateEmployeeTableColumnsProps {
  t: (key: string) => string;
  /** Deactivates an active employee or reactivates an inactive one; leave out to hide the action. */
  onToggleActive?: (employee: Employee) => void;
  /** The signed-in person's Blocks user id: nobody can deactivate their own login. */
  currentUserId?: string;
  workStatus?: WorkStatusColumnsProps;
  /** Copies the employee's email to the clipboard; leave out to hide the copy action. */
  onCopyEmail?: (employee: Employee) => void;
  /** Copies the employee's SLS id to the clipboard; leave out to hide the copy action. */
  onCopyEmployeeId?: (employee: Employee) => void;
  /** Opens the "add fine" dialog for the employee (legacy `HrFineComponent`); leave out to hide
   * the action — gated by `DIRECTORY.CREATE_FINE` at the call site. */
  onFine?: (employee: Employee) => void;
  /** Opens the "assign role" dialog for the employee; leave out to hide the action — gated by
   * `DIRECTORY.ASSIGN_ROLE` at the call site. */
  onAssignRole?: (employee: Employee) => void;
  /** The departments the user may see (`useDepartmentScope().departments`), for the Department
   * column's filter; leave out for no filter. */
  departments?: readonly { ItemId: string; Title: string }[];
}

const workStatusColumn = (
  t: (key: string) => string,
  props: WorkStatusColumnsProps
): ColumnDef<Employee> => ({
  id: 'IsWFH',
  // Card: its own line after the department and designation.
  meta: { mobile: 'full', mobileOrder: 4 },
  header: ({ column }) => <DataTableColumnHeader column={column} title={t('WFH')} />,
  enableSorting: false,
  cell: ({ row }) => (
    <WfhCell
      employee={row.original}
      isSaving={props.savingEmployeeId === row.original.ItemId}
      onChange={props.onWfhChange}
      onEditOfficeDays={props.onOfficeDaysEdit}
    />
  ),
});

export const createEmployeeTableColumns = ({
  t,
  onToggleActive,
  currentUserId,
  workStatus,
  onCopyEmail,
  onCopyEmployeeId,
  onFine,
  onAssignRole,
  departments,
}: CreateEmployeeTableColumnsProps): ColumnDef<Employee>[] => [
  {
    // Card (as the old portal's employee card): the name as the title, the actions menu top right,
    // then Employee ID | Email, Department | Designation, and WFH when shown.
    id: 'DisplayName',
    meta: { filter: { type: 'text' }, mobile: 'title' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
    accessorFn: (row) => employeeName(row),
    // The name alone; the email has its own column.
    cell: ({ row }) => employeeName(row.original),
  },
  {
    id: 'EmployeeID',
    meta: { filter: { type: 'text' }, mobile: 'body', mobileOrder: 0 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_ID')} />,
    accessorFn: (row) => row.EmployeeID ?? '',
    cell: ({ row }) => {
      const employeeId = row.original.EmployeeID;
      if (!employeeId) return null;
      return (
        <div className="flex items-center gap-1">
          {/* Card: wrapped in full rather than cut off. */}
          <span className="truncate max-md:whitespace-normal max-md:break-all">{employeeId}</span>
          {onCopyEmployeeId && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('COPY_EMPLOYEE_ID')}
              title={t('COPY_EMPLOYEE_ID')}
              className="size-6 shrink-0"
              onClick={(event) => {
                // The row itself opens the employee's profile.
                event.stopPropagation();
                onCopyEmployeeId(row.original);
              }}
            >
              <Copy className="size-4" />
            </Button>
          )}
        </div>
      );
    },
  },
  {
    // Sorted and filtered by the department name stored on the profile; the filter is a dropdown of
    // the departments the user may see (the list's department scope still applies on top).
    id: 'DepartmentName',
    meta: {
      mobile: 'body',
      mobileOrder: 2,
      ...(departments
        ? {
            filter: {
              type: 'options' as const,
              dropdown: true,
              options: departments.map((department) => ({
                value: department.Title,
                label: department.Title,
              })),
            },
          }
        : {}),
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.DepartmentName ?? '',
  },
  {
    id: 'Designation',
    // Wide enough that most designations fit on one line and don't stretch the row.
    size: 240,
    // Card: beside the department, as the old portal.
    meta: { filter: { type: 'text' }, mobile: 'body', mobileOrder: 3 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESIGNATION')} />,
    accessorFn: (row) => row.Designation ?? '',
  },
  {
    id: 'Email',
    meta: { filter: { type: 'text' }, mobile: 'body', mobileOrder: 1 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
    accessorFn: (row) => row.Email ?? '',
    cell: ({ row }) => {
      const email = row.original.Email;
      if (!email) return null;
      return (
        <div className="flex items-center gap-1">
          {/* Card: wrapped in full rather than cut off. */}
          <span className="truncate max-md:whitespace-normal max-md:break-all">{email}</span>
          {onCopyEmail && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('COPY_EMAIL')}
              title={t('COPY_EMAIL')}
              className="size-6 shrink-0"
              onClick={(event) => {
                // The row itself opens the employee's profile.
                event.stopPropagation();
                onCopyEmail(row.original);
              }}
            >
              <Copy className="size-4" />
            </Button>
          )}
        </div>
      );
    },
  },
  {
    id: 'IsActive',
    // The login's state, read from IAM per row; not a field on the profile. Not on the card.
    meta: { mobile: 'hidden' },
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => (row.IsActive === false ? t('INACTIVE') : t('ACTIVE')),
    cell: ({ row, getValue }) =>
      row.original.IsActive === undefined ? (
        <InlineSkeleton className="w-14" />
      ) : (
        (getValue() as string)
      ),
  },
  ...(workStatus ? [workStatusColumn(t, workStatus)] : []),
  ...(onToggleActive || onFine || onAssignRole
    ? [
        {
          id: 'actions',
          // Card: top right, beside the name, as the old portal's ⋮ menu.
          meta: { mobile: 'badge' as const },
          header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
          enableSorting: false,
          enableHiding: false,
          cell: ({ row }: { row: { original: Employee } }) => (
            <EmployeeActions
              employee={row.original}
              t={t}
              currentUserId={currentUserId}
              onAssignRole={onAssignRole}
              onFine={onFine}
              onToggleActive={onToggleActive}
            />
          ),
        } as ColumnDef<Employee>,
      ]
    : []),
];

interface EmployeeActionsProps extends Pick<
  CreateEmployeeTableColumnsProps,
  't' | 'currentUserId' | 'onAssignRole' | 'onFine' | 'onToggleActive'
> {
  employee: Employee;
}

/**
 * The row's actions: icon buttons in the table, one ⋮ menu on a phone card (as the old portal's
 * employee card). Each shows only when its handler is given.
 */
const EmployeeActions = ({
  employee,
  t,
  currentUserId,
  onAssignRole,
  onFine,
  onToggleActive,
}: EmployeeActionsProps) => {
  const isMobile = useIsMobile();
  const isInactive = employee.IsActive === false;
  const toggleLabel = isInactive ? t('REACTIVATE_USER') : t('DEACTIVATE_USER');
  // Can't deactivate your own account or change your own roles. This is deliberately NOT
  // the native `disabled` attribute: the Button component's `disabled:pointer-events-none`
  // would let the click fall through the button to the row underneath and navigate to the
  // profile instead of doing nothing — `aria-disabled` keeps the same faded look while
  // still catching (and stopping) the click here.
  const isOwnAccount = Boolean(currentUserId) && employee.UserId === currentUserId;
  const isSelf = !isInactive && isOwnAccount;

  if (isMobile) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('ACTION')}
            onClick={(event) => event.stopPropagation()}
          >
            <DotsThreeVertical className="size-5" weight="bold" />
          </Button>
        </DropdownMenuTrigger>
        {/* React bubbles portal events to the card, so without this a pick also opens the profile. */}
        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
          {onAssignRole && (
            <DropdownMenuItem disabled={isOwnAccount} onSelect={() => onAssignRole(employee)}>
              <UserGear className="size-4" />
              {t('ASSIGN_ROLE')}
            </DropdownMenuItem>
          )}
          {onFine && (
            <DropdownMenuItem onSelect={() => onFine(employee)}>
              <Scales className="size-4" />
              {t('FINE')}
            </DropdownMenuItem>
          )}
          {onToggleActive && (
            <DropdownMenuItem
              disabled={isSelf}
              className={isInactive ? undefined : 'text-destructive'}
              onSelect={() => onToggleActive(employee)}
            >
              {isInactive ? <UserPlus className="size-4" /> : <UserMinus className="size-4" />}
              {toggleLabel}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {onAssignRole && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('ASSIGN_ROLE')}
          title={t('ASSIGN_ROLE')}
          aria-disabled={isOwnAccount}
          className={isOwnAccount ? 'pointer-events-auto opacity-50' : undefined}
          onClick={(event) => {
            event.stopPropagation();
            if (isOwnAccount) return;
            onAssignRole(employee);
          }}
        >
          <UserGear className="size-4" />
        </Button>
      )}
      {onFine && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('FINE')}
          title={t('FINE')}
          onClick={(event) => {
            event.stopPropagation();
            onFine(employee);
          }}
        >
          <Scales className="size-4" />
        </Button>
      )}
      {onToggleActive && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={toggleLabel}
          title={toggleLabel}
          aria-disabled={isSelf}
          className={isSelf ? 'pointer-events-auto opacity-50' : undefined}
          onClick={(event) => {
            // The row itself opens the employee's profile.
            event.stopPropagation();
            if (isSelf) return;
            onToggleActive(employee);
          }}
        >
          {isInactive ? <UserPlus className="size-4" /> : <UserMinus className="size-4" />}
        </Button>
      )}
    </div>
  );
};
